/* eslint-disable react/jsx-props-no-spreading */
import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import {
  VerticalLogo, Icon, WebFooter, Links, useResponsive, MEDIA_TYPES, Responsive,
} from 'lidya-common-components';
import { Trans, useTranslation } from 'gatsby-plugin-react-i18next';
import { SOCIAL, ADDRESSES } from '../common/webSiteVariables';
import {
  getLinkByComponentName,
  LINKS_TO_USE_IN,
  useNavigationLinks,
} from '../../hooks/useNavigationLinks';
import { ContextLink } from '../common/contextLink';
import { useGlobalData } from '../../providers/globalDataProvider/globalDataProvider.useGlobalData';
// import { TrustPilot } from './trustPilot/trustPilot';

import './footer.less';

function NavLinks({ links }) {
  const { country, language } = useGlobalData();
  const navLinks = useNavigationLinks({ country, language });
  return (
    <div className="m-footerNavLinks">
      <Links
        className="a-footerNavLinks__individual"
        menuLinks={links || navLinks}
        CustomLink={links ? undefined : ContextLink}
      />
    </div>
  );
}

NavLinks.propTypes = {
  links: PropTypes.shape([]),
};

NavLinks.defaultProps = {
  links: undefined,
};

function LogoComponent() {
  const { country, language } = useGlobalData();
  const link = getLinkByComponentName({ componentName: 'home', country });
  const slug = link[`slug_${language}`];
  const { new_tab: newTab } = link;

  return (
    <ContextLink
      className="m-navigation__linkLogo"
      ariaLabel={slug}
      id={`HomeLinkFooter${slug}`}
      to={slug}
      target={newTab ? '_blank' : undefined}
    >
      <VerticalLogo contentClassName="a-footerLogo__logo" />
    </ContextLink>
  );
}

function TermAndConditions({ className }) {
  const { country, language } = useGlobalData();
  const links = useNavigationLinks({
    country,
    language,
    toUseIn: LINKS_TO_USE_IN.FOOTER,
  });
  const addressArr = ADDRESSES[country];

  return (
    <>
      {links.map((link) => {
        const { new_tab: newTab, to, slug } = link;
        return (
          <ContextLink
            className={className}
            key={`FooterLinkFooter${slug}`}
            id={`FooterLinkFooter${slug}`}
            to={to}
            target={newTab ? '_blank' : undefined}
          >
            {link.label}
          </ContextLink>
        );
      })}
      <a
        href="mailto:support@lidya.info"
        className="a-footerSecondaryLinks__individual"
      >
        <Trans>web.footer.support</Trans>
      </a>
      {addressArr && addressArr.map((address) => (
        <Fragment key={`address_${address.code}`}>
          {address.ic ? (
            <p className={className}>
              <Trans>web.footer.ic</Trans>
              {': '}
              {address.ic}
            </p>
          ) : null}
          {address.dic ? (
            <p className={className}>
              <Trans>web.footer.dic</Trans>
              {': '}
              {address.dic}
            </p>
          ) : null}
        </Fragment>
      ))}
    </>
  );
}
TermAndConditions.propTypes = {
  className: PropTypes.string,
};
TermAndConditions.defaultProps = {
  className: undefined,
};

function SocialLinks() {
  const { country } = useGlobalData();
  return (
    <div className="m-footerSocial">
      {SOCIAL[country].map((soc) => (
        <div className="a-footerSocial__logo" key={soc.icon}>
          <a
            href={soc.link}
            className="a-footerSocial__link"
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`${soc.icon} page`}
          >
            <Icon name={soc.icon} className="a-footerSocial__icon" />
          </a>
        </div>
      ))}
    </div>
  );
}

function Copyright() {
  const year = new Date().getFullYear();
  return (
    <p>
      <Trans values={{ year }}>web.footer.copyright</Trans>
    </p>
  );
}

function ComplementaryInfo() {
  const { t } = useTranslation();
  const { country } = useGlobalData();
  const addressArr = ADDRESSES[country];

  return (
    <div className="m-footerComplementaryInfo__groupContainer">
      <div className="m-footerComplementaryInfo__right">
        <div className="m-footerAddress">
          {addressArr
            && addressArr.map((address) => (
              <div className="a-footerAddress" key={address.address1}>
                <address className="a-footerAddress__address">
                  <Icon name="marker" className="a-footerAddress__icon" />
                  {address.address1 && <p>{address.address1}</p>}
                  {address.address2 && <p>{address.address2}</p>}
                  <p>
                    <Trans>{address.city}</Trans>
                    {', '}
                    <Trans>{address.country}</Trans>
                  </p>
                  {address.phone && <a href={`tel:${address.phone}`}>{address.phone}</a>}
                  {address.email && <a href={`mailto:${address.email.to}${address.email.subject ? `?subject=${t(address.email.subject)}` : ''}`}>{address.email.to}</a>}
                </address>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}

function GetInTouch() {
  return (
    <>
      <div className="a-footerMobile__title"><Trans>web.footer.touch</Trans></div>
      <ComplementaryInfo />
    </>
  );
}

function FollowUs() {
  return (
    <>
      <div className="a-footerMobile__title"><Trans>web.footer.follow</Trans></div>
      <SocialLinks />
    </>
  );
}

function LogoAndTerms() {
  return (
    <>
      <LogoComponent />
      <Terms />
      <Copyright />
    </>
  );
}

function Terms() {
  return <TermAndConditions className="a-footerSecondaryLinks__individual" />;
}
const buildComponentsArray = ({ links, isLargeSize }) => {
  function NavLinksComp() {
    return <NavLinks links={links} />;
  }
  if (isLargeSize) {
    return [LogoComponent, NavLinksComp, Terms, /* TrustPilot, */ ComplementaryInfo];
  }
  return [GetInTouch, FollowUs, /* TrustPilot, */ LogoAndTerms];
};

function Footer({ links }) {
  const { context: { currentMedia } } = useResponsive();
  const isLargeSize = MEDIA_TYPES.DESKTOP.includes(currentMedia);
  const ComponentsArray = buildComponentsArray({ links, isLargeSize });

  return (
  // <div className="m-globalContentContainer">
    <WebFooter ComponentsArray={ComponentsArray} />
  // </div>
  );
}

Footer.propTypes = {
  links: PropTypes.shape([]),
};

Footer.defaultProps = {
  links: undefined,
};

function FooterAgg(props) {
  return (
    <Responsive>
      <Footer {...props} />
    </Responsive>
  );
}

export default FooterAgg;
